import { getAllCaseFilesList } from './caseMilestoneFileSync';
import { supabase } from './supabase';
import {
  getMissingClinicalPhotoKeys,
  getPhotoLabelByKey,
  hasAllRequiredClinicalPhotos,
} from '../case-dentist/scanUploadConfig';

/** @param {Array<{ category?: string, photoKey?: string }>} files */
export function buildUploadedPhotosByKey(files = []) {
  const map = {};
  for (const f of files) {
    if (f.category === 'photos' && f.photoKey) map[f.photoKey] = f;
  }
  return map;
}

/**
 * @param {Array|Record<string, unknown>} filesOrUploadedByKey
 */
export function validateCaseSubmissionPhotos(filesOrUploadedByKey) {
  const uploadedByKey = Array.isArray(filesOrUploadedByKey)
    ? buildUploadedPhotosByKey(filesOrUploadedByKey)
    : filesOrUploadedByKey || {};
  const missingKeys = getMissingClinicalPhotoKeys(uploadedByKey);
  return {
    ok: missingKeys.length === 0,
    missingKeys,
    missingLabels: missingKeys.map((k) => getPhotoLabelByKey(k) || k),
    hasAllRequired: hasAllRequiredClinicalPhotos(uploadedByKey),
  };
}

export function formatMissingPhotosSubmissionError(missingKeys, missingLabels) {
  const n = missingKeys.length;
  const preview = missingLabels.slice(0, 4).join(', ');
  const more = n > 4 ? ` (+${n - 4} autre${n - 4 > 1 ? 's' : ''})` : '';
  return `Les ${n} photo${n > 1 ? 's' : ''} cliniques obligatoires doivent être téléversées avant l’envoi (${preview}${more}).`;
}

/**
 * @throws {Error} si aucun pack n'est assigné au dossier
 */
export async function assertCaseHasAssignedPackage(caseId) {
  const { data, error } = await supabase
    .from('cases')
    .select('package')
    .eq('id', caseId)
    .single();

  if (error) throw error;
  if (!data?.package) {
    throw new Error(
      'Sélectionnez un pack commercial avant l’envoi (étape Pack et services).',
    );
  }
  return data.package;
}

/**
 * @throws {Error} si une ou plusieurs photos obligatoires manquent
 * @param {{ files?: unknown[], requirePackage?: boolean }} [opts]
 *   requirePackage — true uniquement pour l’envoi guidé dentiste (pack étape 4).
 *   Admin / agent valident la soumission sans pack (assigné plus tard via jalon workflow).
 */
export async function assertCaseReadyForDentistSubmission(caseId, { files, requirePackage = false } = {}) {
  const list = files ?? (await getAllCaseFilesList(caseId));
  const check = validateCaseSubmissionPhotos(list);
  if (!check.ok) {
    throw new Error(formatMissingPhotosSubmissionError(check.missingKeys, check.missingLabels));
  }
  if (requirePackage) {
    await assertCaseHasAssignedPackage(caseId);
  }
  return list;
}
